import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
    UploadCloud,
    Trash2,
    CheckCircle2,
    AlertCircle,
    Eye,
    Copy,
    Check,
    Film,
    Image as ImageIcon,
    ExternalLink,
    RefreshCw,
    Sparkles,
    Smartphone
} from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { compressImageFile } from "../utils/imageCompressor";

const formatBytes = (bytes, decimals = 1) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export default function SplashPosterManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [poster, setPoster] = useState(null);

    // Form inputs
    const [title, setTitle] = useState("Splash Screen Poster");
    const [linkUrl, setLinkUrl] = useState("");
    const [status, setStatus] = useState("Active");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [fileType, setFileType] = useState("image"); // 'image' or 'video'

    // UI States
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [copied, setCopied] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const [compressionInfo, setCompressionInfo] = useState(null);

    const fileInputRef = useRef(null);

    // Fetch current splash screen poster
    const fetchCurrentPoster = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/splash-screen/current`);
            const data = await res.json();
            if (data.status && data.data) {
                setPoster(data.data);
                setTitle(data.data.title || "Splash Screen Poster");
                setLinkUrl(data.data.link_url || "");
                setStatus(data.data.status || "Active");
            } else {
                setPoster(null);
            }
        } catch (err) {
            console.error("Error fetching splash screen poster:", err);
            setPoster(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentPoster();
    }, []);

    // Handle file selection
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isVideo = file.type.startsWith("video/") || /\.(mp4|webm|avi|mov|mkv)$/i.test(file.name);
        const isImage = file.type.startsWith("image/") || /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);

        if (!isVideo && !isImage) {
            Swal.fire("Unsupported File", "Please select an image (JPG, PNG, WEBP) or video (MP4, MOV, WEBM).", "warning");
            return;
        }

        setFileType(isVideo ? "video" : "image");
        setCompressionInfo(null);

        // If it's an image, auto-compress for fast upload
        if (isImage) {
            setCompressing(true);
            try {
                const compressed = await compressImageFile(file, { maxWidth: 1440, maxHeight: 2560, quality: 0.82 });
                setSelectedFile(compressed);
                setPreviewUrl(URL.createObjectURL(compressed));
                if (compressed.size < file.size) {
                    const savedPct = Math.round(((file.size - compressed.size) / file.size) * 100);
                    setCompressionInfo(`Compressed: ${formatBytes(file.size)} ➔ ${formatBytes(compressed.size)} (Saved ${savedPct}%)`);
                }
            } catch (err) {
                console.warn("Compression failed, using original:", err);
                setSelectedFile(file);
                setPreviewUrl(URL.createObjectURL(file));
            } finally {
                setCompressing(false);
            }
        } else {
            // Video
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    // Submit form (Save / Overwrite)
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile && !poster?.media_url) {
            Swal.fire("Missing Media", "Please select an image or video file to upload.", "warning");
            return;
        }

        setIsSubmitting(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append("title", title || "Splash Screen Poster");
        formData.append("status", status);
        formData.append("link_url", linkUrl || "");

        if (selectedFile) {
            formData.append("media", selectedFile);
        } else if (poster?.media_url) {
            formData.append("media_url", poster.media_url);
        }

        Swal.fire({
            title: poster ? "Updating Splash Poster..." : "Uploading Splash Poster...",
            html: `
                <div style="width: 100%; margin-top: 10px; text-align: left;">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <span id="swal-upload-status" style="font-size: 13px; font-weight: 600; color: #475569;">Uploading to Spaces...</span>
                        <span id="swal-upload-pct" style="font-size: 16px; font-weight: 800; color: #2563eb;">0%</span>
                    </div>
                    <div style="width: 100%; background: #e2e8f0; height: 14px; border-radius: 9999px; overflow: hidden; position: relative;">
                        <div id="swal-progress-bar" style="width: 0%; height: 100%; background: linear-gradient(90deg, #3b82f6, #06b6d4); border-radius: 9999px; transition: width 0.15s ease;"></div>
                    </div>
                    <div style="margin-top: 8px; font-size: 12px; color: #64748b; text-align: right;">
                        <span id="swal-upload-bytes">Uploading media file...</span>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false
        });

        try {
            const res = await axios.post(`${API_BASE_URL}/api/splash-screen/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percent);

                        const pctEl = document.getElementById("swal-upload-pct");
                        const barEl = document.getElementById("swal-progress-bar");
                        const bytesEl = document.getElementById("swal-upload-bytes");
                        const statusEl = document.getElementById("swal-upload-status");

                        if (pctEl) pctEl.innerText = `${percent}%`;
                        if (barEl) barEl.style.width = `${percent}%`;
                        if (bytesEl) bytesEl.innerText = `${formatBytes(progressEvent.loaded)} / ${formatBytes(progressEvent.total)}`;
                        if (statusEl && percent >= 100) {
                            statusEl.innerText = "Processing & saving on server...";
                        }
                    }
                }
            });

            if (res.data && res.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Splash Poster Updated!",
                    text: "The new splash poster has been updated and will be shown in the app.",
                    timer: 2000,
                    showConfirmButton: false
                });
                setSelectedFile(null);
                setPreviewUrl("");
                setCompressionInfo(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                fetchCurrentPoster();
            } else {
                Swal.fire("Error", res.data?.message || "Failed to update splash poster.", "error");
            }
        } catch (error) {
            console.error("Error saving splash poster:", error);
            Swal.fire("Upload Failed", error.response?.data?.message || error.message || "Failed to save splash poster", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Toggle active / inactive status
    const handleToggleStatus = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/splash-screen/toggle-status`, {
                method: "PUT"
            });
            const data = await res.json();
            if (data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Status Updated",
                    text: `Splash poster is now ${data.data.status}`,
                    timer: 1500,
                    showConfirmButton: false
                });
                fetchCurrentPoster();
            } else {
                Swal.fire("Error", data.message || "Could not toggle status", "error");
            }
        } catch (err) {
            console.error("Error toggling status:", err);
            Swal.fire("Error", "Failed to update status", "error");
        }
    };

    // Delete current splash poster
    const handleDelete = async () => {
        const confirm = await Swal.fire({
            title: "Delete Splash Poster?",
            text: "This will remove the splash screen poster from the app. You can upload a new one anytime.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, Delete"
        });

        if (confirm.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE_URL}/api/splash-screen`, {
                    method: "DELETE"
                });
                const data = await res.json();
                if (data.status) {
                    Swal.fire("Deleted!", "Splash screen poster has been removed.", "success");
                    setPoster(null);
                    setSelectedFile(null);
                    setPreviewUrl("");
                } else {
                    Swal.fire("Error", data.message || "Could not delete splash poster", "error");
                }
            } catch (err) {
                console.error("Error deleting splash poster:", err);
                Swal.fire("Error", "Failed to delete splash poster", "error");
            }
        }
    };

    // Copy URL to clipboard
    const handleCopyUrl = (url) => {
        if (!url) return;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900 font-sans overflow-hidden">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 flex flex-col h-full overflow-hidden lg:pl-64">
                <Header onMenuClick={() => setIsSidebarOpen(true)} />

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
                    {/* Header Banner */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="p-2 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 rounded-xl">
                                    <Sparkles size={22} />
                                </span>
                                <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">
                                    Splash Screen Poster
                                </h1>
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Upload an image or video to display on the app's splash screen popup. Uploading a new poster automatically updates over the current one.
                            </p>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchCurrentPoster}
                                disabled={loading}
                                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-semibold rounded-xl transition flex items-center gap-2 disabled:opacity-50"
                            >
                                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>

                    {/* Status Stats Summary */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Status Card */}
                        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                            <div className="space-y-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Poster Status</span>
                                <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                                        poster?.status === 'Active'
                                            ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                                            : 'bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
                                    }`}>
                                        <span className={`w-2 h-2 rounded-full ${poster?.status === 'Active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                        {poster ? poster.status : 'Not Configured'}
                                    </span>
                                </div>
                            </div>
                            {poster && (
                                <button
                                    onClick={handleToggleStatus}
                                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium transition"
                                >
                                    Toggle {poster.status === 'Active' ? 'Inactive' : 'Active'}
                                </button>
                            )}
                        </div>

                        {/* Media Type Card */}
                        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                            <div className="space-y-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Media Type</span>
                                <div className="flex items-center gap-2 text-base font-bold text-slate-800 dark:text-white">
                                    {poster?.media_type === 'video' ? (
                                        <>
                                            <Film size={18} className="text-purple-500" />
                                            <span>Video Poster</span>
                                        </>
                                    ) : (
                                        <>
                                            <ImageIcon size={18} className="text-blue-500" />
                                            <span>Image Poster</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <span className="p-2.5 bg-slate-50 dark:bg-slate-700/50 text-slate-400 rounded-xl">
                                <Smartphone size={20} />
                            </span>
                        </div>

                        {/* Last Updated Card */}
                        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm flex items-center justify-between">
                            <div className="space-y-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Last Updated</span>
                                <p className="text-sm font-bold text-slate-800 dark:text-white">
                                    {poster?.updated_at || poster?.created_at ? new Date(poster.updated_at || poster.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Never'}
                                </p>
                            </div>
                            <span className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-500 rounded-xl">
                                <CheckCircle2 size={20} />
                            </span>
                        </div>
                    </div>

                    {/* Main Content: 2 Columns */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Left Column: Live Splash Poster Preview (7 Cols) */}
                        <div className="lg:col-span-7 space-y-4">
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-4">
                                    <div className="flex items-center gap-2">
                                        <Eye size={18} className="text-blue-600 dark:text-blue-400" />
                                        <h2 className="text-base font-bold text-slate-800 dark:text-white">
                                            Current Splash Screen Preview
                                        </h2>
                                    </div>
                                    {poster && (
                                        <button
                                            onClick={handleDelete}
                                            className="px-3 py-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 rounded-xl text-xs font-semibold transition flex items-center gap-1.5"
                                        >
                                            <Trash2 size={14} />
                                            <span>Delete Poster</span>
                                        </button>
                                    )}
                                </div>

                                {loading ? (
                                    <div className="h-96 flex flex-col items-center justify-center gap-3 text-slate-400">
                                        <RefreshCw size={28} className="animate-spin text-blue-600" />
                                        <span className="text-sm">Loading splash screen poster...</span>
                                    </div>
                                ) : poster && poster.media_url ? (
                                    <div className="space-y-4">
                                        {/* Mock Phone Frame / Player Container */}
                                        <div className="relative mx-auto max-w-sm rounded-3xl overflow-hidden bg-slate-900 border-4 border-slate-800 dark:border-slate-700 shadow-2xl">
                                            {/* Media Rendering */}
                                            {poster.media_type === 'video' ? (
                                                <div className="relative aspect-[9/16] bg-black flex items-center justify-center">
                                                    <video
                                                        src={poster.media_url}
                                                        controls
                                                        autoPlay
                                                        loop
                                                        muted
                                                        playsInline
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="relative aspect-[9/16] bg-slate-950 flex items-center justify-center">
                                                    <img
                                                        src={poster.media_url}
                                                        alt={poster.title || "Splash Poster"}
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>
                                            )}

                                            {/* Live Tag overlay */}
                                            <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-semibold text-white flex items-center gap-1.5">
                                                <span className={`w-2 h-2 rounded-full ${poster.status === 'Active' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                                                <span>{poster.status === 'Active' ? 'Live in App' : 'Inactive'}</span>
                                            </div>

                                            {/* Media Type pill overlay */}
                                            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-semibold text-white flex items-center gap-1">
                                                {poster.media_type === 'video' ? <Film size={12} /> : <ImageIcon size={12} />}
                                                <span className="uppercase">{poster.media_type}</span>
                                            </div>
                                        </div>

                                        {/* Poster Meta Info */}
                                        <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl border border-slate-200/60 dark:border-slate-700/60 space-y-2 text-xs">
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Title:</span>
                                                <span className="font-semibold text-slate-800 dark:text-white">{poster.title || "Splash Screen Poster"}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-slate-500">Redirect Link:</span>
                                                {poster.link_url ? (
                                                    <a
                                                        href={poster.link_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="font-medium text-blue-600 hover:underline flex items-center gap-1 max-w-[200px] truncate"
                                                    >
                                                        <span>{poster.link_url}</span>
                                                        <ExternalLink size={12} />
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-400 italic">None</span>
                                                )}
                                            </div>
                                            <div className="flex justify-between items-center pt-1 border-t border-slate-200/50 dark:border-slate-700/50">
                                                <span className="text-slate-500">Media URL:</span>
                                                <button
                                                    onClick={() => handleCopyUrl(poster.media_url)}
                                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 font-semibold flex items-center gap-1"
                                                >
                                                    {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                                                    <span>{copied ? "Copied!" : "Copy URL"}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-16 text-center space-y-3">
                                        <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                            <Smartphone size={32} />
                                        </div>
                                        <h3 className="text-base font-bold text-slate-700 dark:text-slate-200">
                                            No Splash Poster Set
                                        </h3>
                                        <p className="text-xs text-slate-500 max-w-sm mx-auto">
                                            Upload an image or video using the form on the right. It will immediately be displayed on the app splash screen.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column: Upload / Replace Form (5 Cols) */}
                        <div className="lg:col-span-5 space-y-4">
                            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-5">
                                <div className="border-b border-slate-100 dark:border-slate-700/60 pb-3">
                                    <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        <UploadCloud size={18} className="text-blue-600" />
                                        <span>{poster ? "Replace / Update Splash Poster" : "Upload Splash Poster"}</span>
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Select a new image or video. Uploading will automatically update over the existing poster.
                                    </p>
                                </div>

                                {/* Smart Replacement Alert */}
                                <div className="p-3 bg-blue-50/80 dark:bg-blue-950/30 rounded-xl border border-blue-200/70 dark:border-blue-800/40 text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Auto-Overwrite:</strong> Only 1 splash poster is kept. Whenever you upload a new one, it cleanly overwrites the previous one.
                                    </span>
                                </div>

                                {/* File Dropzone */}
                                <div className="space-y-2">
                                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Poster Media (Image or Video) *
                                    </label>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className={`cursor-pointer border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
                                            selectedFile
                                                ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-950/20'
                                                : 'border-slate-300 dark:border-slate-600 hover:border-blue-500 dark:hover:border-blue-400 bg-slate-50/60 dark:bg-slate-900/40'
                                        }`}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*,video/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />

                                        {previewUrl ? (
                                            <div className="space-y-3">
                                                <div className="relative mx-auto max-h-48 rounded-xl overflow-hidden shadow-md flex items-center justify-center bg-black">
                                                    {fileType === 'video' ? (
                                                        <video
                                                            src={previewUrl}
                                                            controls
                                                            className="max-h-48 w-auto object-contain"
                                                        />
                                                    ) : (
                                                        <img
                                                            src={previewUrl}
                                                            alt="Preview"
                                                            className="max-h-48 w-auto object-contain"
                                                        />
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                                                    <span>{selectedFile?.name} ({formatBytes(selectedFile?.size)})</span>
                                                </div>
                                                {compressionInfo && (
                                                    <div className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 rounded-full text-[11px] font-semibold border border-emerald-200">
                                                        ⚡ {compressionInfo}
                                                    </div>
                                                )}
                                                <p className="text-[11px] text-blue-600 font-semibold">Click to choose a different file</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2 py-4">
                                                <div className="w-12 h-12 mx-auto rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                    <UploadCloud size={24} />
                                                </div>
                                                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                                    Click to browse or drag & drop file
                                                </p>
                                                <p className="text-[11px] text-slate-400">
                                                    Supports MP4, MOV, WEBM videos & JPG, PNG, WEBP images
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Poster Title */}
                                <div className="space-y-1.5">
                                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Poster Title
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="e.g. Diwali Special Splash Banner"
                                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white"
                                    />
                                </div>

                                {/* Redirect Link URL */}
                                <div className="space-y-1.5">
                                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Redirect Link URL (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={linkUrl}
                                        onChange={(e) => setLinkUrl(e.target.value)}
                                        placeholder="e.g. https://socialbaazar.com/offers"
                                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white"
                                    />
                                </div>

                                {/* Status Toggle */}
                                <div className="space-y-1.5">
                                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                        Display Status
                                    </label>
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-white"
                                    >
                                        <option value="Active">Active (Visible to users)</option>
                                        <option value="Inactive">Inactive (Hidden from users)</option>
                                    </select>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting || compressing || (!selectedFile && !poster?.media_url)}
                                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition duration-200 shadow-md shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <RefreshCw size={16} className="animate-spin" />
                                            <span>Uploading ({uploadProgress}%)...</span>
                                        </>
                                    ) : compressing ? (
                                        <>
                                            <RefreshCw size={16} className="animate-spin" />
                                            <span>Compressing Image...</span>
                                        </>
                                    ) : (
                                        <>
                                            <UploadCloud size={16} />
                                            <span>{poster ? "Update & Overwrite Splash Poster" : "Publish Splash Poster"}</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
